import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, ArrowUpRight, ChevronDown, Search, Facebook, Music2, Share2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { ASSETS } from "@/data/assets";

const iconMap = {
  "google-ads": Search,
  "meta-ads": Facebook,
  "tiktok-ads": Music2,
  "social-media": Share2,
};

const LangSwitch = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div className={`flex items-center rounded-full border border-border/70 bg-secondary/40 p-0.5 ${className}`}>
      {["tr", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
            lang === l
              ? "bg-primary text-primary-foreground shadow-glow-soft"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Logo = () => (
  <a href="#home" className="group flex items-center">
    <img
      src={ASSETS.logo}
      alt="LUKI"
      className="h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
    />
  </a>
);

const navLinkClass =
  "group relative flex items-center gap-1 px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground";

const Underline = () => (
  <span className="absolute inset-x-4 -bottom-0.5 h-px origin-left scale-x-0 bg-gradient-warm transition-transform duration-300 group-hover:scale-x-100" />
);

export const Navbar = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const services = t.services.items;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-border/50 bg-background/70 py-3 backdrop-blur-xl"
          : "py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6">
        <Logo />

        <nav className="hidden items-center gap-1 lg:flex">
          <a href="#about" className={navLinkClass}>
            {t.nav.about}
            <Underline />
          </a>

          {/* Services with hover dropdown */}
          <div className="group/svc relative">
            <a href="#services" className={navLinkClass}>
              {t.nav.services}
              <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover/svc:rotate-180" />
              <Underline />
            </a>
            <div className="invisible absolute left-1/2 top-full z-50 w-[20rem] -translate-x-1/2 translate-y-1 pt-3 opacity-0 transition-all duration-200 group-hover/svc:visible group-hover/svc:translate-y-0 group-hover/svc:opacity-100">
              <div className="glass-strong rounded-2xl p-2 shadow-card">
                {services.map((s) => {
                  const Icon = iconMap[s.slug] || Search;
                  return (
                    <a
                      key={s.slug}
                      href={`#${s.slug}`}
                      className="flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-secondary/60"
                    >
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                        <Icon className="h-4.5 w-4.5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground">{s.title}</span>
                        <span className="block text-xs text-muted-foreground">{s.sub}</span>
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <a href="#contact" className={navLinkClass}>
            {t.nav.contact}
            <Underline />
          </a>

          <a href="#advertising" className={navLinkClass}>
            {t.nav.advertising}
            <Underline />
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitch className="hidden sm:flex" />
          <Button variant="hero" size="lg" className="hidden sm:inline-flex" asChild>
            <a href="#contact">
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="glassOutline" size="icon" className="lg:hidden" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] max-w-sm border-border/50 bg-background/95 backdrop-blur-xl">
              <div className="mt-8 flex flex-col gap-1">
                <a
                  href="#about"
                  onClick={() => setOpen(false)}
                  className="rounded-xl px-4 py-3 text-lg font-medium text-foreground/90 transition-colors hover:bg-secondary/60 hover:text-primary"
                >
                  {t.nav.about}
                </a>

                <span className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t.nav.services}
                </span>
                {services.map((s) => {
                  const Icon = iconMap[s.slug] || Search;
                  return (
                    <a
                      key={s.slug}
                      href={`#${s.slug}`}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-foreground/90 transition-colors hover:bg-secondary/60 hover:text-primary"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
                        <Icon className="h-4 w-4" />
                      </span>
                      {s.title}
                    </a>
                  );
                })}

                <a
                  href="#contact"
                  onClick={() => setOpen(false)}
                  className="mt-1 rounded-xl px-4 py-3 text-lg font-medium text-foreground/90 transition-colors hover:bg-secondary/60 hover:text-primary"
                >
                  {t.nav.contact}
                </a>

                <a
                  href="#advertising"
                  onClick={() => setOpen(false)}
                  className="rounded-xl px-4 py-3 text-lg font-medium text-foreground/90 transition-colors hover:bg-secondary/60 hover:text-primary"
                >
                  {t.nav.advertising}
                </a>

                <div className="mt-4 flex items-center justify-between gap-3 px-1">
                  <LangSwitch />
                </div>
                <Button variant="hero" size="lg" className="mt-4" asChild>
                  <a href="#contact" onClick={() => setOpen(false)}>
                    {t.nav.cta}
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
